import { SpecialAnchorTypes, ToolBaseTypes, ToolTypes } from '@labradorsports/constants';
import { arraySlidingWindow } from '@labradorsports/utils';

import { Move } from '../models/index.js';
import {
    createLines,
    snapMoveToPlayerLine,
    defaultMove,
    getMoveLength,
    calculatePartialMovement,
    getMidpoint,
} from '../utils/index.js';

import { PluginContext } from './api.js';

export default {
    baseType: ToolBaseTypes.MOVE,
    types: [ToolTypes.DMOVE, ToolTypes.OMOVE, ToolTypes.MOVE],
    specialAnchorTypes: [SpecialAnchorTypes.MOVE_ELBOW],
    generateAnchors: ({ play, frame }) => {
        const anchors = [];

        if (!play.fieldSetup) {
            anchors.unshift(
                ...frame.visibleMoves(play.viewOptions).flatMap((mv) => {
                    const anchors = mv.anchors.map((anc: any, idx: number) => {
                        return {
                            type: mv.type,
                            id: idx,
                            target: mv.target,
                            ...anc,
                        };
                    });

                    if (mv.anchors.length === 1) {
                        anchors.push({
                            type: SpecialAnchorTypes.MOVE_ELBOW,
                            id: 0,
                            target: mv.target,
                            ...getMidpoint(mv.origin, mv.anchors[0]),
                            props: {
                                moveType: mv.type,
                                anchorCount: mv.anchors.length,
                            },
                        });
                    }

                    return anchors;
                })
            );
        }

        return anchors;
    },

    generateSelectedAnchors: ({ play }, selected) => {
        if (selected instanceof Move && play.config.MoveConfig[selected.type].baseType === ToolBaseTypes.MOVE) {
            return arraySlidingWindow([selected.origin, ...selected.anchors], (anchor, nextAnchor, idx) => {
                return {
                    target: selected.target,
                    type: SpecialAnchorTypes.MOVE_ELBOW,
                    id: idx,
                    x: (anchor.x + nextAnchor.x) / 2,
                    y: (anchor.y + nextAnchor.y) / 2,
                    props: {
                        moveType: selected.type,
                        anchorCount: selected.anchors.length,
                    },
                };
            });
        }

        return [];
    },

    generateLines: ({ play, frame }) => {
        if (play.fieldSetup) return [];

        return frame.visibleMoves(play.viewOptions).flatMap(createLines);
    },

    handleDragEnd: ({ api, frame, fieldViewport, play }, anchor, offset) => {
        if (anchor.type === SpecialAnchorTypes.MOVE_ELBOW) {
            let newMove = frame.getMove(anchor.target);

            if (newMove.anchors.length === anchor.props.anchorCount) {
                newMove = frame.insertAnchor(anchor.target, anchor.id, offset);
            } else {
                newMove = frame.updateAnchor(anchor.target, anchor.id, offset);
            }

            api.updateMove(newMove);
        } else if (typeof anchor.id === 'number') {
            const move = frame.getMove(anchor.target);
            const newMove = move.copy();

            if (anchor.type === ToolTypes.OMOVE || anchor.type === ToolTypes.DMOVE) {
                newMove.type = ToolTypes.MOVE;
            }

            // For moves, attempt to snap to a line
            if (anchor.type === ToolTypes.MOVE) {
                const snapLine = snapMoveToPlayerLine(frame, fieldViewport, offset, anchor.target);

                // Snap to the line, and mark the move to remove the player
                if (snapLine) {
                    newMove.props.targetLine = snapLine.id;
                    newMove.updateAnchor(anchor.id, snapLine.origin, play.field);
                } else {
                    newMove.props.targetLine = null;
                    newMove.updateAnchor(anchor.id, offset, play.field);
                }
            } else {
                newMove.updateAnchor(anchor.id, offset, play.field);
            }

            api.updateMove(newMove);
        } else {
            const closest = frame.findClosestPlayer(
                offset,
                frame.hiddenGoalies(),
                frame.getTargetForToolType(anchor.type)
            );

            if (closest) {
                const newMove = defaultMove(frame.field, closest.origin, anchor.type, closest.id);

                if (anchor.type === ToolTypes.OMOVE || anchor.type === ToolTypes.DMOVE) {
                    newMove.type = ToolTypes.MOVE;
                }

                api.updateMove(newMove);
            }
        }
    },

    handleDrag({ frame, fieldViewport, frames }, anchor, offset, clamped) {
        let newMove;

        if (anchor.type === SpecialAnchorTypes.MOVE_ELBOW) {
            const thisFrame = frames[frame.idx];
            const move = thisFrame.getMove(anchor.target);

            if (move.anchors.length === anchor.props.anchorCount) {
                newMove = thisFrame.insertAnchor(anchor.target, anchor.id, clamped);
            } else {
                newMove = thisFrame.updateAnchor(anchor.target, anchor.id, clamped);
            }
        } else if (anchor.type === ToolTypes.MOVE) {
            const snapLine = snapMoveToPlayerLine(frame, fieldViewport, offset, anchor.target);

            const target = snapLine ? snapLine.origin : clamped;

            newMove = frame.updateAnchor(anchor.target, anchor.id, target);
        } else {
            // All other move types
            newMove = frame.updateAnchor(anchor.target, anchor.id, clamped);

            if (anchor.type === ToolTypes.ZONE) {
                const move = frame.getMove(anchor.target);

                if (anchor.id === move.anchors.length - 1) {
                    const shading = frame.pieces.find((piece) => piece.props?.moveId === anchor.target);

                    frame.updateFieldPiece(shading.id, clamped);
                }
            }
        }

        const newMoveLength = getMoveLength(newMove);

        const receiverPass = frame.passes?.find((p) => p.target === anchor.target);
        if (receiverPass) {
            const newEnd = Math.min(receiverPass.props?.end ?? newMoveLength, newMoveLength);
            const newStart = Math.min(receiverPass.props?.start ?? 0, newEnd);
            const carrier = frame.getCarrier(receiverPass.props.ball);
            const carrierMove = frame.getMove(carrier?.id);

            frame.updatePass(receiverPass.id, {
                endpoint: calculatePartialMovement(newMove, newEnd),
                origin: carrierMove ? calculatePartialMovement(carrierMove, newStart) : carrier.origin,
            });
        }

        const carrierPass = frame.passes?.find((p) => frame.getCarrier(p.props.ball).id === anchor.target);
        if (carrierPass) {
            const newStart = Math.min(carrierPass.props?.start, newMoveLength);
            frame.updatePass(carrierPass.id, {
                origin: calculatePartialMovement(newMove, newStart),
            });
        }

        frame.routeOptions
            ?.filter((ro) => ro.source === anchor.target)
            .forEach((ro) => {
                const newStart = Math.min(ro.props?.start ?? 0, newMoveLength);
                frame.updateRouteOption(ro.id, {
                    origin: calculatePartialMovement(newMove, newStart),
                });
            });
    },

    getDefaultMove({ frame }, type, offset) {
        const closest = frame.findClosestPlayer(offset, frame.hiddenGoalies(), frame.getTargetForToolType(type));

        if (!closest) return null;

        return defaultMove(frame.field, closest, type, closest.id);
    },
} as PlayPlugin<PluginContext>;
